import { Maximize2, Minus, Plus } from 'lucide-react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { type ChronologyEventLight, isBackgroundEvent } from '@/hooks/useChronology';
import { cn } from '@/lib/utils';
import type { RegionalDateLocale } from '@/utils/date-utils';
import { formatTemporalCompact } from '@/utils/temporal';
import { SearchHighlightedText } from './search-highlight';
import { getEventAccentStyle } from './timeline';
import {
  bucketOverflow,
  chooseTickSpec,
  clampWindow,
  clusterZoomTarget,
  DAY_MS,
  densityBins,
  formatMajorLabel,
  formatTickLabel,
  generateTicks,
  majorSpecFor,
  openingWindow,
  packLanes,
  windowForCanvasResize,
  type TimelineItem,
  type TimeWindow,
  timelineExtent,
  toTimelineItem,
  zoomWindow,
} from './timeline-scale';

const CARD_WIDTH = 236;
const CARD_HEIGHT = 64;
const CARD_GAP = 12;
const LANE_HEIGHT = 76;
const CANVAS_TOP_PADDING = 16;
const CLUSTER_BAND = 30;
const AXIS_HEIGHT = 48;
const OVERVIEW_HEIGHT = 60;
const OVERVIEW_BAR_TOP = 10;
const OVERVIEW_BAR_HEIGHT = 26;
const MIN_TICK_SPACING_PX = 72;
const MIN_SPAN = 3 * DAY_MS;

const ZOOM_STEP = 1.6;
const ANIMATION_MS = 320;
const DRAG_THRESHOLD_PX = 4;
const BRUSH_HANDLE_PX = 7;

const GRID_MINOR_STROKE = '#f1f3f5';
const GRID_MAJOR_STROKE = '#e5e7eb';
const AXIS_STROKE = '#d1d5db';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function useElementSize(el: HTMLElement | null) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setSize(prev => (prev.width === rect.width && prev.height === rect.height ? prev : { width: rect.width, height: rect.height }));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return size;
}

function overviewLabel(t: number, unit: 'day' | 'month' | 'year'): string {
  const label = formatTickLabel(t, { unit, step: 1 });
  if (unit === 'year') return label;
  const year = new Date(t).getUTCFullYear();
  return unit === 'month' ? `${label} ${year}` : formatMajorLabel(t, { unit: 'month', step: 1 });
}

export interface HorizontalTimelineProps {
  events: ChronologyEventLight[];
  undatedCount: number;
  selectedEventId: string | null;
  onSelectEvent: (event: ChronologyEventLight) => void;
  onShowUndated: () => void;
  colorByCategory?: ReadonlyMap<string, string | null>;
  activeCategories?: ReadonlySet<string>;
  excludedTypes?: ReadonlySet<string>;
  isAggregate: boolean;
  locale: RegionalDateLocale;
  /** Element beside the event count that hosts the zoom controls. */
  controlsHost?: HTMLElement | null;
}

export function HorizontalTimeline({
  events,
  undatedCount,
  selectedEventId,
  onSelectEvent,
  onShowUndated,
  colorByCategory,
  activeCategories,
  excludedTypes,
  isAggregate,
  locale,
  controlsHost,
}: HorizontalTimelineProps) {
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);
  const { width, height } = useElementSize(canvasEl);
  const overviewRef = useRef<HTMLDivElement>(null);

  const items = useMemo(
    () => events
      .map(toTimelineItem)
      .filter((item): item is TimelineItem => item !== null)
      .sort((a, b) => a.start - b.start),
    [events],
  );
  const extent = useMemo(() => timelineExtent(items), [items]);
  const maxSpan = extent ? Math.max((extent.end - extent.start) * 2.2, 60 * DAY_MS) : 0;

  const [view, setView] = useState<TimeWindow | null>(null);
  const [dragging, setDragging] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const viewRef = useRef<TimeWindow | null>(null);
  const extentRef = useRef(extent);
  const maxSpanRef = useRef(maxSpan);
  const widthRef = useRef(width);
  const animationRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const [openClusterStart, setOpenClusterStart] = useState<number | null>(null);
  extentRef.current = extent;
  maxSpanRef.current = maxSpan;
  widthRef.current = width;

  const commit = useCallback((next: TimeWindow) => {
    const bounds = extentRef.current;
    if (!bounds) return;
    const clamped = clampWindow(next, bounds, MIN_SPAN, maxSpanRef.current);
    viewRef.current = clamped;
    setView(clamped);
  }, []);

  const cancelAnimation = useCallback(() => {
    if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
    animationRef.current = null;
  }, []);

  const animateTo = useCallback((target: TimeWindow) => {
    cancelAnimation();
    const from = viewRef.current;
    if (!from || prefersReducedMotion()) {
      commit(target);
      return;
    }
    const startedAt = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / ANIMATION_MS);
      const eased = 1 - (1 - progress) ** 3;
      commit({
        start: from.start + (target.start - from.start) * eased,
        end: from.end + (target.end - from.end) * eased,
      });
      animationRef.current = progress < 1 ? requestAnimationFrame(step) : null;
    };
    animationRef.current = requestAnimationFrame(step);
  }, [cancelAnimation, commit]);

  useEffect(() => cancelAnimation, [cancelAnimation]);
  useEffect(() => () => dragCleanupRef.current?.(), []);

  // Cards extend rightwards from their dot, so the fitted window reserves a card's width past the last event.
  const fitWindow = useCallback((): TimeWindow | null => {
    const bounds = extentRef.current;
    const px = widthRef.current;
    if (!bounds || px === 0) return bounds;
    const usable = Math.max(px - CARD_WIDTH - CARD_GAP, px * 0.5);
    return { start: bounds.start, end: bounds.start + (bounds.end - bounds.start) * (px / usable) };
  }, []);

  const openingStartsRef = useRef<number[]>([]);
  openingStartsRef.current = items.map(item => item.start);
  // The first handful of dates decide how far the opening window reaches.
  const openingKey = items.slice(0, 8).map(item => item.start).join(':');

  // Open on the first events. Fit still shows the whole chronology.
  const openingView = useCallback((): TimeWindow | null => {
    const fitted = fitWindow();
    if (!fitted) return null;
    return openingWindow(openingStartsRef.current, fitted, widthRef.current, CARD_WIDTH + CARD_GAP);
  }, [fitWindow]);

  // A click opens the event panel and narrows the canvas. Keep the scale so
  // cards stay in their lanes, and keep a selected card inside the new width.
  // This runs before paint and cancels an in-flight pan, which would otherwise
  // commit the old span on the next frame.
  const previousCanvasWidthRef = useRef(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const selectedEventIdRef = useRef(selectedEventId);
  selectedEventIdRef.current = selectedEventId;
  useLayoutEffect(() => {
    const previous = previousCanvasWidthRef.current;
    previousCanvasWidthRef.current = width;
    if (!previous || !width || previous === width) return;
    const current = viewRef.current;
    if (!current) return;
    const selected = itemsRef.current.find(entry => entry.event.id === selectedEventIdRef.current);
    cancelAnimation();
    // A date that has scrolled off the left leaves its card pinned to the edge.
    // Follow that card, not the date, or a resize pans back to the date.
    const msPerPx = (current.end - current.start) / previous;
    const cardX = selected ? Math.max((selected.start - current.start) / msPerPx, 0) : null;
    commit(windowForCanvasResize(
      current,
      previous,
      width,
      cardX === null ? null : { x: cardX, maxX: width - CARD_WIDTH - 24 },
    ));
  }, [width, cancelAnimation, commit]);

  const extentKey = extent ? `${extent.start}:${extent.end}` : '';
  const hasWidth = width > 0;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the opening events or the canvas measurement change.
  useEffect(() => {
    const target = openingView();
    if (!target || !hasWidth) return;
    if (!viewRef.current) commit(target);
    else animateTo(target);
  }, [openingKey, extentKey, hasWidth]);

  const fit = useCallback(() => {
    const target = fitWindow();
    if (target) animateTo(target);
  }, [animateTo, fitWindow]);

  const zoomBy = useCallback((factor: number, anchorRatio = 0.5) => {
    const current = viewRef.current;
    if (current) animateTo(zoomWindow(current, factor, anchorRatio));
  }, [animateTo]);

  // Wheel: scroll pans through time; pinch or Ctrl/⌘ + scroll zooms at the pointer.
  // Handled across the whole timeline so a horizontal trackpad swipe never reaches
  // the browser, where macOS treats it as back/forward navigation.
  useEffect(() => {
    if (!rootEl || !canvasEl) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const current = viewRef.current;
      if (!current) return;
      cancelAnimation();
      const rect = canvasEl.getBoundingClientRect();
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.width : 1;
      if (event.ctrlKey || event.metaKey) {
        const delta = Math.max(-60, Math.min(60, event.deltaY * unit));
        commit(zoomWindow(current, Math.exp(delta * 0.01), (event.clientX - rect.left) / rect.width));
        return;
      }
      const delta = (Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY) * unit;
      const shift = delta * ((current.end - current.start) / rect.width);
      commit({ start: current.start + shift, end: current.end + shift });
    };
    rootEl.addEventListener('wheel', onWheel, { passive: false });
    return () => rootEl.removeEventListener('wheel', onWheel);
  }, [rootEl, canvasEl, cancelAnimation, commit]);

  // A cancelled wheel event doesn't always stop the macOS history swipe; turning
  // off horizontal overscroll on the root does.
  useEffect(() => {
    const targets = [document.documentElement, document.body];
    const previous = targets.map(target => target.style.overscrollBehaviorX);
    for (const target of targets) target.style.overscrollBehaviorX = 'none';
    return () => {
      targets.forEach((target, index) => {
        target.style.overscrollBehaviorX = previous[index];
      });
    };
  }, []);

  const handleCanvasPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !viewRef.current || width === 0) return;
    cancelAnimation();
    const originX = event.clientX;
    const origin = viewRef.current;
    const msPerPx = (origin.end - origin.start) / width;
    let moved = false;
    const onMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - originX;
      if (!moved && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      if (!moved) setDragging(true);
      moved = true;
      commit({ start: origin.start - dx * msPerPx, end: origin.end - dx * msPerPx });
    };
    const stop = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (dragCleanupRef.current === stop) dragCleanupRef.current = null;
    };
    const onUp = () => {
      stop();
      if (moved) {
        suppressClickRef.current = true;
        setTimeout(() => { suppressClickRef.current = false; }, 0);
        setDragging(false);
      }
    };
    dragCleanupRef.current?.();
    dragCleanupRef.current = stop;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [cancelAnimation, commit, width]);

  const handleOverviewPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const bounds = extentRef.current;
    const current = viewRef.current;
    const el = overviewRef.current;
    if (event.button !== 0 || !bounds || !current || !el) return;
    event.preventDefault();
    cancelAnimation();
    const rect = el.getBoundingClientRect();
    const span = bounds.end - bounds.start;
    const toX = (t: number) => ((t - bounds.start) / span) * rect.width;
    const x = event.clientX - rect.left;
    // Match the brush that is drawn: a window past the data is clipped to the strip.
    const left = Math.max(0, toX(current.start));
    const right = Math.min(rect.width, toX(current.end));
    let mode: 'move' | 'left' | 'right' = 'move';
    let origin = current;
    if (Math.abs(x - left) <= BRUSH_HANDLE_PX) mode = 'left';
    else if (Math.abs(x - right) <= BRUSH_HANDLE_PX) mode = 'right';
    else if (x < left || x > right) {
      const centre = bounds.start + (x / rect.width) * span;
      const half = (current.end - current.start) / 2;
      commit({ start: centre - half, end: centre + half });
      origin = viewRef.current ?? current;
    }
    const originX = event.clientX;
    const onMove = (moveEvent: PointerEvent) => {
      const dt = ((moveEvent.clientX - originX) / rect.width) * span;
      if (mode === 'move') commit({ start: origin.start + dt, end: origin.end + dt });
      else if (mode === 'left') commit({ start: Math.min(origin.start + dt, origin.end - MIN_SPAN), end: origin.end });
      else commit({ start: origin.start, end: Math.max(origin.end + dt, origin.start + MIN_SPAN) });
    };
    const stop = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (dragCleanupRef.current === stop) dragCleanupRef.current = null;
    };
    const onUp = () => {
      stop();
      setDragging(false);
    };
    dragCleanupRef.current?.();
    dragCleanupRef.current = stop;
    setDragging(true);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [cancelAnimation, commit]);

  const msPerPx = view && width > 0 ? (view.end - view.start) / width : 1;
  const axisY = Math.max(height - AXIS_HEIGHT, CLUSTER_BAND + LANE_HEIGHT);
  const laneCount = Math.max(1, Math.floor((axisY - CLUSTER_BAND - CANVAS_TOP_PADDING) / LANE_HEIGHT));

  const packing = useMemo(() => {
    const cardSpan = (CARD_WIDTH + CARD_GAP) * msPerPx;
    const build = (prioritised: string | null) => packLanes(
      items.map(item => ({
        id: item.event.id,
        start: item.start,
        end: item.start + cardSpan,
        priority: item.event.id === prioritised ? 0 : isBackgroundEvent(item.event) ? 2 : 1,
      })),
      laneCount,
    );
    // Selecting a card must not reshuffle the lanes; only lift the selection when it was hidden.
    const result = build(null);
    return selectedEventId && result.overflow.has(selectedEventId) ? build(selectedEventId) : result;
  }, [items, msPerPx, laneCount, selectedEventId]);

  const xOf = useCallback((t: number) => (view ? (t - view.start) / msPerPx : 0), [view, msPerPx]);

  const visibleItems = useMemo(() => {
    if (!view || width === 0) return [];
    return items.filter(item => {
      const x0 = xOf(item.start);
      const x1 = item.end ? xOf(item.end) : x0;
      return x1 >= -CARD_WIDTH && x0 <= width + 8;
    });
  }, [items, view, width, xOf]);

  // Cards whose dot has scrolled off the left edge stay pinned there until the next card in their lane pushes them out.
  const cardLefts = useMemo(() => {
    const byLane = new Map<number, TimelineItem[]>();
    for (const item of visibleItems) {
      const lane = packing.lanes.get(item.event.id);
      if (lane === undefined) continue;
      const laneItems = byLane.get(lane);
      if (laneItems) laneItems.push(item);
      else byLane.set(lane, [item]);
    }
    const lefts = new Map<string, number>();
    for (const laneItems of byLane.values()) {
      laneItems.forEach((item, index) => {
        const next = laneItems[index + 1];
        const limit = next ? xOf(next.start) - CARD_WIDTH - CARD_GAP : Infinity;
        lefts.set(item.event.id, Math.min(Math.max(xOf(item.start), 0), limit));
      });
    }
    return lefts;
  }, [visibleItems, packing, xOf]);

  const ticks = useMemo(() => {
    if (!view || width === 0) return null;
    const minor = chooseTickSpec(msPerPx, MIN_TICK_SPACING_PX);
    const major = majorSpecFor(minor);
    return {
      minor,
      major,
      minorTicks: generateTicks(view, minor),
      majorTicks: major ? generateTicks(view, major, true) : [],
    };
  }, [view, width, msPerPx]);

  const clusters = useMemo(() => {
    if (!view || !ticks) return [];
    const hidden = items.filter(item => packing.overflow.has(item.event.id));
    return bucketOverflow(hidden.map(item => item.start), view, ticks.minor).map(bucket => {
      const left = Math.max(xOf(bucket.start), 0);
      const right = Math.min(xOf(bucket.end), width);
      const events = hidden.filter(item => item.start >= bucket.start && item.start < bucket.end);
      return { ...bucket, x: (left + right) / 2, events };
    });
  }, [view, ticks, items, packing, xOf, width]);

  const overview = useMemo(() => {
    if (!extent || width === 0) return null;
    const binCount = Math.max(24, Math.floor(width / 5));
    const bins = densityBins(items, extent, binCount);
    const peak = Math.max(1, ...bins);
    const overviewMsPerPx = (extent.end - extent.start) / width;
    const spec = chooseTickSpec(overviewMsPerPx, 84);
    const unit = spec.unit;
    const labelSpec = unit === 'day' ? { unit: 'month' as const, step: 1 } : spec;
    return {
      binWidth: width / binCount,
      bars: bins.map(count => (count === 0 ? 0 : Math.max(2, Math.sqrt(count / peak) * OVERVIEW_BAR_HEIGHT))),
      labels: generateTicks(extent, labelSpec).map(t => ({
        t,
        x: ((t - extent.start) / (extent.end - extent.start)) * width,
        label: overviewLabel(t, labelSpec.unit),
      })),
    };
  }, [items, extent, width]);

  // Keep a selection made elsewhere (list, side panel, keyboard) in view.
  // Opening the timeline often measures the canvas after the first run, so a
  // selection is remembered until a real window exists, then handled once.
  // The date is part of that memory: a filter refresh keeps it and must not
  // pan back, but a date edit has to bring the card back on screen.
  const revealedSelectionRef = useRef<{ id: string; start: number } | null>(null);
  useEffect(() => {
    if (!selectedEventId) {
      revealedSelectionRef.current = null;
      return;
    }
    const current = viewRef.current;
    if (!current || width === 0) return;
    const item = items.find(entry => entry.event.id === selectedEventId);
    if (!item) return;
    const revealed = revealedSelectionRef.current;
    if (revealed?.id === selectedEventId && revealed.start === item.start) return;
    revealedSelectionRef.current = { id: selectedEventId, start: item.start };
    const span = current.end - current.start;
    const x = ((item.start - current.start) / span) * width;
    if (x < 24 || x > width - CARD_WIDTH - 24) {
      animateTo({ start: item.start - span * 0.4, end: item.start + span * 0.6 });
    }
  }, [selectedEventId, width, extentKey, items, animateTo]);

  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const index = items.findIndex(item => item.event.id === selectedEventId);
      const current = viewRef.current;
      const next = index === -1
        ? items.find(item => current && item.start >= current.start) ?? items[0]
        : items[index + (event.key === 'ArrowRight' ? 1 : -1)];
      if (next) onSelectEvent(next.event);
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      zoomBy(1 / ZOOM_STEP);
    } else if (event.key === '-' || event.key === '_') {
      event.preventDefault();
      zoomBy(ZOOM_STEP);
    } else if (event.key === '0') {
      event.preventDefault();
      fit();
    }
  }, [items, selectedEventId, onSelectEvent, zoomBy, fit]);

  if (items.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="text-sm text-gray-500">None of these events has a date that can be placed on the timeline.</p>
        {undatedCount > 0 && (
          <button type="button" onClick={onShowUndated} className="text-sm font-medium text-gray-600 underline decoration-gray-300 underline-offset-2 hover:text-gray-900">
            Show them in the list
          </button>
        )}
      </div>
    );
  }

  const cardTop = (lane: number) => axisY - CLUSTER_BAND - lane * LANE_HEIGHT - CARD_HEIGHT;
  const activeId = hoveredId ?? selectedEventId;

  const zoomControls = (
    <div className="flex min-w-0 items-center gap-2">
      <div className="inline-flex h-7 shrink-0 items-center overflow-hidden rounded-md border border-gray-200 bg-white">
        <button
          type="button"
          onClick={() => zoomBy(ZOOM_STEP)}
          aria-label="Zoom out"
          title="Zoom out"
          className="flex h-full w-7 items-center justify-center text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <span className="h-4 w-px bg-gray-200" aria-hidden="true" />
        <button
          type="button"
          onClick={() => zoomBy(1 / ZOOM_STEP)}
          aria-label="Zoom in"
          title="Zoom in"
          className="flex h-full w-7 items-center justify-center text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
      <button
        type="button"
        onClick={fit}
        title="Show every event"
        className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
      >
        <Maximize2 className="h-3 w-3" />
        Fit all
      </button>
      {undatedCount > 0 && (
        <button
          type="button"
          onClick={onShowUndated}
          className="min-w-0 truncate text-xs text-gray-400 underline decoration-gray-200 underline-offset-2 hover:text-gray-700 hover:decoration-gray-400"
        >
          {undatedCount} undated {undatedCount === 1 ? 'event is' : 'events are'} in the list view
        </button>
      )}
    </div>
  );

  return (
    <div ref={setRootEl} className="flex h-full min-h-[280px] flex-col">
      {controlsHost && createPortal(zoomControls, controlsHost)}

      <div
        ref={setCanvasEl}
        role="application"
        aria-roledescription="timeline"
        aria-label="Chronology timeline. Use the left and right arrow keys to move between events, plus and minus to zoom."
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the canvas takes focus for arrow-key navigation and zoom shortcuts.
        tabIndex={0}
        onPointerDown={handleCanvasPointerDown}
        onDoubleClick={event => {
          if (event.target !== event.currentTarget || width === 0) return;
          const rect = event.currentTarget.getBoundingClientRect();
          zoomBy(1 / ZOOM_STEP, (event.clientX - rect.left) / width);
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative min-h-0 flex-1 touch-none select-none overflow-hidden border-t border-gray-100 bg-[linear-gradient(to_bottom,#ffffff,#fcfcfd)]',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-200',
          dragging ? 'cursor-grabbing' : 'cursor-grab',
        )}
      >
        {view && ticks && width > 0 && (
          <>
            <svg className="pointer-events-none absolute inset-0" width={width} height={height} aria-hidden="true">
              {ticks.minorTicks.map(t => (
                <line key={`minor-${t}`} x1={xOf(t)} x2={xOf(t)} y1={0} y2={axisY} stroke={GRID_MINOR_STROKE} />
              ))}
              {ticks.majorTicks.map(t => (
                <line key={`major-${t}`} x1={xOf(t)} x2={xOf(t)} y1={0} y2={axisY + AXIS_HEIGHT} stroke={GRID_MAJOR_STROKE} />
              ))}
              <line x1={0} x2={width} y1={axisY} y2={axisY} stroke={AXIS_STROKE} />
              {ticks.minorTicks.map(t => (
                <line key={`tick-${t}`} x1={xOf(t)} x2={xOf(t)} y1={axisY} y2={axisY + 5} stroke={AXIS_STROKE} />
              ))}
            </svg>

            {ticks.minorTicks.map(t => (
              <span
                key={`label-${t}`}
                className="pointer-events-none absolute whitespace-nowrap text-[11px] tabular-nums text-gray-500"
                style={{ left: xOf(t) + 5, top: axisY + 7 }}
              >
                {formatTickLabel(t, ticks.minor)}
              </span>
            ))}

            {ticks.major && ticks.majorTicks.map((t, index) => {
              const label = formatMajorLabel(t, ticks.major ?? ticks.minor);
              const next = ticks.majorTicks[index + 1];
              const labelWidth = label.length * 7 + 12;
              const pinned = Math.max(xOf(t), 0);
              const left = next === undefined ? pinned : Math.min(pinned, xOf(next) - labelWidth);
              return (
                <span
                  key={`major-label-${t}`}
                  className="pointer-events-none absolute whitespace-nowrap text-[10.5px] font-semibold uppercase tracking-[0.08em] text-gray-500"
                  style={{ left: left + 5, top: axisY + 26 }}
                >
                  {label}
                </span>
              );
            })}

            {visibleItems.map(item => {
              if (item.end === null) return null;
              const style = getEventAccentStyle(item.event, colorByCategory, activeCategories, excludedTypes);
              const x0 = xOf(item.start);
              const x1 = xOf(item.end);
              const isRange = item.kind === 'range';
              return (
                <span
                  key={`span-${item.event.id}`}
                  className={cn(
                    'pointer-events-none absolute rounded-full',
                    style.dot,
                    isRange ? 'h-1' : 'h-0.5 opacity-50',
                    item.event.id === activeId && 'opacity-100',
                  )}
                  style={{ left: x0, width: Math.max(x1 - x0, 2), top: axisY - (isRange ? 2 : 1) }}
                />
              );
            })}

            {visibleItems.map(item => {
              const lane = packing.lanes.get(item.event.id);
              const cardLeft = cardLefts.get(item.event.id);
              if (lane === undefined || cardLeft === undefined) return null;
              const stemX = Math.max(xOf(item.start), 0);
              if (stemX < cardLeft || stemX > cardLeft + CARD_WIDTH) return null;
              const top = cardTop(lane) + CARD_HEIGHT;
              const active = item.event.id === activeId;
              return (
                <span
                  key={`stem-${item.event.id}`}
                  className={cn(
                    'pointer-events-none absolute w-px transition-[top,height,background-color] duration-200',
                    active ? 'bg-gray-500' : 'bg-gray-300/80',
                  )}
                  style={{ left: stemX, top, height: axisY - top }}
                />
              );
            })}

            {visibleItems.map(item => {
              const x = xOf(item.start);
              if (x < -8 || x > width + 8) return null;
              const style = getEventAccentStyle(item.event, colorByCategory, activeCategories, excludedTypes);
              const isSelected = item.event.id === selectedEventId;
              const isActive = item.event.id === activeId;
              const size = isSelected ? 11 : isActive ? 9 : 7;
              return (
                <span
                  key={`dot-${item.event.id}`}
                  className={cn(
                    'pointer-events-none absolute flex items-center justify-center rounded-full ring-2 ring-white transition-[width,height] duration-150',
                    style.activeDot,
                    isSelected && 'shadow-[0_0_0_4px_rgba(17,24,39,0.08)]',
                  )}
                  style={{ left: x - size / 2, top: axisY - size / 2, width: size, height: size }}
                >
                  {item.kind === 'approximate' && <span className="h-[3px] w-[3px] rounded-full bg-white" />}
                </span>
              );
            })}

            {visibleItems.map(item => {
              const lane = packing.lanes.get(item.event.id);
              const cardLeft = cardLefts.get(item.event.id);
              if (lane === undefined || cardLeft === undefined || cardLeft + CARD_WIDTH < 0) return null;
              const ev = item.event;
              const style = getEventAccentStyle(ev, colorByCategory, activeCategories, excludedTypes);
              const isSelected = ev.id === selectedEventId;
              const background = isBackgroundEvent(ev);
              const dateLabel = formatTemporalCompact(ev, null, locale) ?? ev.temporal_display ?? '';
              const top = cardTop(lane);
              return (
                <div
                  key={ev.id}
                  className={cn('group absolute hover:z-30', isSelected && 'z-40')}
                  style={{ left: cardLeft, top, width: CARD_WIDTH, height: CARD_HEIGHT }}
                >
                <button
                  type="button"
                  data-event-id={ev.id}
                  aria-pressed={isSelected}
                  aria-label={`${dateLabel}: ${ev.description}`}
                  onClick={() => {
                    if (suppressClickRef.current) return;
                    onSelectEvent(ev);
                  }}
                  onMouseEnter={() => setHoveredId(ev.id)}
                  onMouseLeave={() => setHoveredId(current => (current === ev.id ? null : current))}
                  onFocus={() => setHoveredId(ev.id)}
                  onBlur={() => setHoveredId(current => (current === ev.id ? null : current))}
                  className={cn(
                    'absolute bottom-0 left-0 flex w-full min-h-full cursor-pointer flex-col rounded-[3px] rounded-r-md border border-l-2 px-2.5 py-1.5 text-left',
                    'transition-[box-shadow,border-color] duration-200 ease-out',
                    'hover:shadow-[0_8px_24px_-6px_rgba(15,23,42,0.18),0_2px_6px_-2px_rgba(15,23,42,0.08)]',
                    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-300',
                    isSelected
                      ? cn(
                          'h-auto overflow-visible border-gray-300 bg-white shadow-[0_8px_24px_-6px_rgba(15,23,42,0.18),0_2px_6px_-2px_rgba(15,23,42,0.08)]',
                          style.activeBorderL,
                        )
                      : cn(
                          'h-full overflow-hidden border-gray-200/90 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:h-auto hover:overflow-visible',
                          background ? 'bg-white/60' : 'bg-white',
                          style.borderL,
                        ),
                  )}
                >
                  <span className="flex min-w-0 items-center gap-1.5 text-[11px] leading-4">
                    <span className="shrink-0 font-medium tabular-nums text-gray-500">{dateLabel}</span>
                    {isAggregate && ev.chronology_name && (
                      <span className="min-w-0 truncate text-gray-400">· {ev.chronology_name}</span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'mt-0.5 text-[12.5px] leading-[17px]',
                      isSelected ? 'line-clamp-none' : 'line-clamp-2 group-hover:line-clamp-none',
                      background ? 'text-gray-500' : 'text-gray-800',
                    )}
                  >
                    <SearchHighlightedText text={ev.description} />
                  </span>
                </button>
                </div>
              );
            })}

            {clusters.map(cluster => {
              const noun = cluster.count === 1 ? 'event' : 'events';
              const zoomTarget = view && extent
                ? clusterZoomTarget(
                  cluster.events.map(item => item.start),
                  view,
                  width,
                  { minSpan: MIN_SPAN, cardPitchPx: CARD_WIDTH + CARD_GAP },
                )
                : null;
              const clampedZoom = zoomTarget && extent
                ? clampWindow(zoomTarget, extent, MIN_SPAN, maxSpan)
                : null;
              const currentSpan = view ? view.end - view.start : 0;
              const canZoom = clampedZoom != null && view != null && currentSpan > 0
                && (
                  Math.abs(clampedZoom.start - view.start) > currentSpan * 0.04
                  || Math.abs((clampedZoom.end - clampedZoom.start) - currentSpan) > currentSpan * 0.04
                );
              return (
                <Popover
                  key={`cluster-${cluster.start}`}
                  open={openClusterStart === cluster.start}
                  onOpenChange={(open) => {
                    if (open && (canZoom || suppressClickRef.current)) return;
                    setOpenClusterStart(open ? cluster.start : null);
                  }}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        if (suppressClickRef.current) {
                          event.preventDefault();
                          return;
                        }
                        if (!canZoom || !clampedZoom) return;
                        event.preventDefault();
                        animateTo(clampedZoom);
                      }}
                      title={canZoom
                        ? `${cluster.count} more ${noun} in this period. Click to zoom in.`
                        : `${cluster.count} more ${noun} share this time. Click to choose one.`}
                      aria-label={canZoom
                        ? `Zoom in to ${cluster.count} more ${noun}`
                        : `Choose from ${cluster.count} more ${noun}`}
                      className="absolute z-10 -translate-x-1/2 rounded-full border border-gray-200 bg-white px-1.5 text-[10px] font-medium leading-4 tabular-nums text-gray-500 shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition-colors hover:border-gray-400 hover:text-gray-900"
                      style={{ left: cluster.x, top: axisY - CLUSTER_BAND + 6 }}
                    >
                      +{cluster.count}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="center" className="w-72 p-1">
                    <p className="px-2 py-1.5 text-[11px] text-gray-500">
                      {cluster.count} {noun} at this time
                    </p>
                    <div className="subtle-scrollbar max-h-64 overflow-y-auto">
                      {cluster.events.map(item => (
                        <button
                          key={item.event.id}
                          type="button"
                          className="flex w-full flex-col rounded-md px-2 py-1.5 text-left hover:bg-gray-50"
                          onClick={() => {
                            onSelectEvent(item.event);
                            setOpenClusterStart(null);
                          }}
                        >
                          <span className="text-[11px] tabular-nums text-gray-500">
                            {formatTemporalCompact(item.event, null, locale) ?? item.event.temporal_display ?? ''}
                          </span>
                          <span className="line-clamp-2 text-[13px] leading-[17px] text-gray-800">
                            <SearchHighlightedText text={item.event.description} />
                          </span>
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              );
            })}
          </>
        )}
      </div>

      <div
        ref={overviewRef}
        onPointerDown={handleOverviewPointerDown}
        className="relative shrink-0 touch-none select-none border-t border-gray-100 bg-slate-900/[0.02]"
        style={{ height: OVERVIEW_HEIGHT }}
        title="Drag the window to move through time, or drag its edges to zoom"
      >
        {overview && view && extent && (() => {
          const span = extent.end - extent.start;
          const left = Math.max(0, ((view.start - extent.start) / span) * width);
          const right = Math.min(width, ((view.end - extent.start) / span) * width);
          return (
            <>
              <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
                {overview.bars.map((barHeight, index) => {
                  if (barHeight === 0) return null;
                  const x = index * overview.binWidth;
                  const inside = x + overview.binWidth > left && x < right;
                  return (
                    <rect
                      // biome-ignore lint/suspicious/noArrayIndexKey: bins are positional.
                      key={index}
                      x={x + 0.5}
                      y={OVERVIEW_BAR_TOP + OVERVIEW_BAR_HEIGHT - barHeight}
                      width={Math.max(overview.binWidth - 1.5, 1)}
                      height={barHeight}
                      rx={1}
                      fill={inside ? '#6b7280' : '#d1d5db'}
                    />
                  );
                })}
              </svg>
              {overview.labels.map(label => (
                <span
                  key={label.t}
                  className="pointer-events-none absolute whitespace-nowrap text-[10px] tabular-nums text-gray-400"
                  style={{ left: label.x + 4, top: OVERVIEW_BAR_TOP + OVERVIEW_BAR_HEIGHT + 5 }}
                >
                  {label.label}
                </span>
              ))}
              <div
                className={cn(
                  'absolute inset-y-1.5 rounded-[5px] border border-gray-400/70 bg-gray-900/[0.035] shadow-[0_1px_2px_rgba(15,23,42,0.06)]',
                  dragging ? 'cursor-grabbing' : 'cursor-grab',
                )}
                style={{ left, width: Math.max(right - left, 6) }}
              >
                <span className="absolute inset-y-0 -left-1 w-2 cursor-ew-resize" aria-hidden="true">
                  <span className="absolute left-1/2 top-1/2 h-3.5 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-400" />
                </span>
                <span className="absolute inset-y-0 -right-1 w-2 cursor-ew-resize" aria-hidden="true">
                  <span className="absolute left-1/2 top-1/2 h-3.5 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-400" />
                </span>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
